import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Home, BookOpen, Download, Compass } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const POPULAR_LINKS = [
  { to: '/how-it-works', label: 'How Levanta works' },
  { to: '/levanta-story', label: "Levanta's story" },
  { to: '/founder', label: 'Meet the founder' },
  { to: '/blog', label: 'The blog' },
  { to: '/affiliates', label: 'Affiliate program' },
  { to: '/faq', label: 'Frequently asked questions' },
  { to: '/contact', label: 'Contact the team' },
  { to: '/download-app', label: 'Download Levanta' },
];

const NotFound = () => {
  useEffect(() => {
    document.title = '404 — Page Not Found | Levanta';
    /* Tell crawlers this is intentionally a 404 — even though the
       client-side router serves it with a 200 status, the noindex
       hint keeps Google from indexing dead URLs. */
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, follow';
    meta.id = 'lv-404-noindex';
    document.head.appendChild(meta);
    return () => {
      const node = document.getElementById('lv-404-noindex');
      if (node) node.remove();
    };
  }, []);

  return (
    <div className="bg-white overflow-x-hidden" data-testid="not-found-page">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-12 md:pb-20 px-6 overflow-hidden min-h-[640px] md:min-h-[760px]">
        <AltParallaxBackdrop />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/85 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="not-found-eyebrow"
          >
            <Compass className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            404 — Off the trail
          </span>

          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            This path isn&rsquo;t on the{' '}
            <em className="italic font-light text-blue-600">skill tree.</em>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            The page you were looking for has moved, been renamed, or never
            existed. Easy fix — pick where you want to go next.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              data-testid="not-found-home-btn"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#0b1f3a] text-white font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              <Home className="w-4 h-4" />
              Back to home
            </Link>
            <Link
              to="/blog"
              data-testid="not-found-blog-btn"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/85 backdrop-blur text-[#0b1f3a] font-semibold ring-1 ring-black/10 hover:bg-white transition-all duration-200"
            >
              <BookOpen className="w-4 h-4" />
              Read the blog
            </Link>
            <Link
              to="/download-app"
              data-testid="not-found-download-btn"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/85 backdrop-blur text-[#0b1f3a] font-semibold ring-1 ring-black/10 hover:bg-white transition-all duration-200"
            >
              <Download className="w-4 h-4" />
              Download app
            </Link>
          </div>
        </div>
      </section>

      {/* POPULAR PAGES */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Popular destinations
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-12">
            Most people end up{' '}
            <em className="italic font-light text-blue-600">here.</em>
          </h2>

          <div className="grid sm:grid-cols-2 gap-3" data-testid="not-found-popular-list">
            {POPULAR_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`not-found-link-${link.to.replace(/\//g, '-').replace(/^-/, '')}`}
                className="group flex items-center justify-between gap-4 rounded-2xl bg-[#f4f8fc] hover:bg-white hover:ring-1 hover:ring-blue-200 hover:shadow-md transition-all duration-200 px-5 py-4"
              >
                <span className="flex items-center gap-3 text-[#0b1f3a] font-semibold text-[15px]">
                  <ArrowLeft className="w-4 h-4 rotate-180 text-blue-600 transition-transform duration-200 group-hover:translate-x-0.5" />
                  {link.label}
                </span>
                <ArrowRight className="w-4 h-4 text-[#0b1f3a]/35 group-hover:text-blue-600 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default NotFound;
